import { Injectable } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { UniqueDomainValidatorService } from './unique-domain-validator.service';

export type PlatformPackageId = 'community' | 'society' | 'growth';

function cognitoPasswordValidator(control: AbstractControl): ValidationErrors | null {
  const password = String(control.value ?? '');

  if (!password) {
    return null;
  }

  const errors: ValidationErrors = {};

  if (!/[A-Z]/.test(password)) {
    errors['missingUppercase'] = true;
  }

  if (!/[a-z]/.test(password)) {
    errors['missingLowercase'] = true;
  }

  if (!/\d/.test(password)) {
    errors['missingNumber'] = true;
  }

  if (!/[!@#$%^&*-]/.test(password)) {
    errors['missingSpecialCharacter'] = true;
  }

  return Object.keys(errors).length > 0 ? errors : null;
}

@Injectable({
  providedIn: 'root',
})
export class FormStateService {
  private readonly form: FormGroup;

  constructor(
    private readonly fb: FormBuilder,
    private readonly uniqueDomainValidator: UniqueDomainValidatorService
  ) {
    this.form = this.fb.group({
      packageInformation: this.fb.group({
        platformPackage: ['society' as PlatformPackageId, Validators.required]
      }),
      organizationInformation: this.fb.group({
        name: ['', Validators.required],
        domain: [
          '',
          [
            Validators.required,
            Validators.pattern(/^[a-z0-9-]{1,50}$/i)
          ],
          [this.uniqueDomainValidator.validate.bind(this.uniqueDomainValidator)] // Async validator should be in the third argument
        ],
        industry: ['', Validators.required],
        nonProfit: [false]
      }),
      adminInformation: this.fb.group({
        firstName: ['', Validators.required],
        lastName: ['', Validators.required],
        email: ['', [Validators.required, Validators.email]],
        password: ['', [Validators.required, Validators.minLength(8), cognitoPasswordValidator]],
        phone: [''] // Now optional
      }),
      brandingInformation: this.fb.group({
        logo: [''],
        icon: [''],
        colorPalette: ['']
      })
    });
  }

  setPlatformPackage(packageId: PlatformPackageId): void {
    const resolved = packageId === 'community' ? 'society' : packageId;
    this.form.get('packageInformation.platformPackage')?.setValue(resolved);
  }

  getPlatformPackage(): PlatformPackageId {
    const value = this.form.get('packageInformation.platformPackage')?.value as PlatformPackageId | null;
    if (!value || value === 'community') {
      return 'society';
    }
    return value;
  }

  requiresPaymentMethod(): boolean {
    const pkg = this.getPlatformPackage();
    return pkg === 'society' || pkg === 'growth';
  }

  getClonedForm(): FormGroup {
    const clonedForm = this.cloneForm(this.form);
    clonedForm.disable(); // Ensure it's disabled
    return clonedForm;
  }

  private cloneForm(group: FormGroup): FormGroup {
    const clonedGroup = new FormGroup({});
    Object.keys(group.controls).forEach((key) => {
      const control = group.get(key);
      if (control instanceof FormControl) {
        clonedGroup.addControl(
          key,
          new FormControl(control.value, control.validator, control.asyncValidator)
        );
      } 
      else if (control instanceof FormGroup) {
        clonedGroup.addControl(key, this.cloneForm(control)); // Recursively clone nested FormGroups
      }
    });
    return clonedGroup;
  }
  
  getForm(): FormGroup {
    return this.form;
  }
}
